/**
 * Despacho: lo que cuesta llegar a cada parte de Chile (ADM-10).
 *
 * Tres perillas del mismo problema, de lo general a lo particular:
 *
 *   1. Condiciones generales: si se ofrece despacho a domicilio y desde que
 *      monto es gratis, y si se ofrece retiro en la sala de ventas.
 *   2. La tarifa de cada region, sobre un mapa de Chile con el precio escrito
 *      en cada una. Un clic abre la region en un modal y se guarda sola.
 *   3. Las comunas con precio propio, dentro del modal de su region: cuando
 *      una region tiene destinos que no cuestan lo mismo, se ven ahi.
 *
 * **Los montos que se ven al entrar son un supuesto**, no un dato del cliente:
 * la tabla real de costos todavia no la entrega. Esta pantalla existe
 * justamente para que la respuesta, cuando llegue, se escriba desde aca y no en
 * un despliegue.
 */
import { useEffect, useState } from 'react';

import type { DespachoAdmin } from '@bandu/compartido';

import { BarraAdmin } from '../../componentes/administracion/BarraAdmin';
import { CondicionesDespacho } from '../../componentes/administracion/CondicionesDespacho';
import { MapaDespacho } from '../../componentes/administracion/MapaDespacho';
import { ModalTarifaComuna } from '../../componentes/administracion/ModalTarifaComuna';
import type { ComunaEnEdicion } from '../../componentes/administracion/ModalTarifaComuna';
import { ModalTarifaRegion } from '../../componentes/administracion/ModalTarifaRegion';
import { PanelAdmin } from '../../componentes/administracion/PanelAdmin';
import { AvisoError, Cargando } from '../../componentes/comunes/EstadoPeticion';
import { Icono } from '../../componentes/comunes/Icono';
import { usePeticion } from '../../hooks/use-peticion';
import { Seo } from '../../seo/Seo';
import { obtenerDespachoAdmin } from '../../servicios/despacho-admin.servicio';
import { rutas } from '../../utilidades/rutas';

export function Despacho() {
  const [version, setVersion] = useState(0);
  const peticion = usePeticion((senal) => obtenerDespachoAdmin(senal), [version]);

  // La respuesta de cada guardado de region o de condiciones trae el panel
  // completo y se pinta con ella. Las comunas no: tras ellas se vuelve a pedir.
  const [datos, setDatos] = useState<DespachoAdmin | null>(null);
  const [falla, setFalla] = useState<string | null>(null);
  const [regionAbierta, setRegionAbierta] = useState<string | null>(null);
  const [comuna, setComuna] = useState<ComunaEnEdicion | null>(null);

  useEffect(() => {
    if (peticion.datos !== null) setDatos(peticion.datos);
  }, [peticion.datos]);

  if (peticion.estado === 'cargando' && datos === null) return <Cargando que="las tarifas" />;
  if (peticion.estado === 'error' && datos === null) return <AvisoError error={peticion.error} />;
  if (datos === null) return null;

  const region = datos.regiones.find((candidata) => candidata.codigo === regionAbierta) ?? null;

  // Un `<dialog>` a la vez: la region se cierra mientras se edita una de sus
  // comunas y se vuelve a abrir al terminar, ya con el dato nuevo.
  function editarComuna(edicion: ComunaEnEdicion): void {
    setRegionAbierta(null);
    setComuna(edicion);
  }

  function cerrarComuna(): void {
    if (comuna !== null) setRegionAbierta(comuna.codigoRegion);
    setComuna(null);
  }

  function recargar(): void {
    setVersion((valor) => valor + 1);
  }

  return (
    <>
      <Seo
        titulo="Despacho"
        descripcion="Tarifas de despacho por region y comuna."
        ruta={rutas.administracion.despacho()}
        sinIndexar
      />

      <BarraAdmin
        titulo="Despacho"
        bajada="Lo que se cobra por llevar el pedido, y desde cuando no se cobra."
      />

      <div className="admin-cuerpo">
        {falla !== null ? (
          <p className="acceso-error" role="alert">
            <Icono nombre="alerta" />
            {falla}
          </p>
        ) : null}

        <CondicionesDespacho datos={datos} alGuardar={setDatos} alFallar={setFalla} />

        <PanelAdmin
          titulo="Tarifa por region"
          bajada="Toca una region para cambiar su precio, su plazo, apagarla o dar precio propio a alguna de sus comunas."
        >
          {/* Las tarifas se pueden preparar con el despacho apagado: se guardan
              igual y valen el dia que se vuelva a encender. */}
          {!datos.despachoHabilitado ? (
            <p className="aviso-despacho info">
              <Icono nombre="info" />
              El despacho a domicilio esta apagado: estas tarifas no se cobran hasta que se vuelva a
              ofrecer.
            </p>
          ) : null}
          <MapaDespacho regiones={datos.regiones} alElegir={setRegionAbierta} />
        </PanelAdmin>
      </div>

      <ModalTarifaRegion
        region={region}
        excepciones={datos.excepciones}
        alGuardar={setDatos}
        alAgregarComuna={(codigoRegion) => {
          editarComuna({ codigoRegion });
        }}
        alEditarComuna={(excepcion) => {
          editarComuna(excepcion);
        }}
        alQuitarComuna={recargar}
        onCerrar={() => {
          setRegionAbierta(null);
        }}
      />

      <ModalTarifaComuna
        edicion={comuna}
        regiones={datos.regiones}
        alGuardar={recargar}
        onCerrar={cerrarComuna}
      />
    </>
  );
}
